<template>
    <div>
        <email-accordion-item
            :class="{ 'border-t': index !== 0 }"
            :communication="communication"
            :lead-full-name="leadFullName"
            :is-loading="communication.loading_body"
            ref="items"
            :key="communication.id"
            @load="loadEmailBody(communication)"
            v-for="(communication, index) in listCommunications"
        />
        <div class="flex items-center justify-center" v-if="showMoreButton">
            <activix-button class="mb-3" type="white" @click="loadItems">
                {{ $t('addLead.showMore') }}
            </activix-button>
        </div>
    </div>
</template>

<script>
    import { orderBy } from 'lodash-es';

    import CommunicationMethod from '@/entities/CommunicationMethod.js';
    import EmailAccordionItem from '@/components/lead/EmailAccordionItem.vue';
    import TimedPageActions from '@/constants/TimedPageActions.ts';
    import { useTimedPageAction } from '@/hooks/useTimedPageAction.ts';

    // Pinia
    import { mapActions, mapState } from 'pinia';
    import { useClientCardStore } from '../../store/modules/clientCard/store.js';
    import { useGlobalStore } from '../../store/store.ts';

    export default {
        components: { EmailAccordionItem },

        props: {
            communications: {
                type: Array,
                required: true,
            },
            showMore: {
                type: Boolean,
                default: false,
            },
            leadFullName: {
                type: String,
                required: true,
            },
        },

        data() {
            return {
                items: [],
                loadingBody: 0,
                connectedCommunications: [],
                itemsCount: 5,
                loadMoreCount: 5,
            };
        },

        computed: {
            ...mapState(useGlobalStore, ['guest']),
            ...mapState(useClientCardStore, ['currentCommunicationId']),

            showMoreButton() {
                return (
                    this.showMore && this.itemsCount < this.orderedConnectedCommunications.length
                );
            },

            listCommunications() {
                if (this.showMore) {
                    return this.orderedConnectedCommunications.slice(0, this.itemsCount);
                }

                return this.orderedConnectedCommunications;
            },

            orderedConnectedCommunications() {
                return orderBy(this.connectedCommunications, 'sort_created_at', 'desc');
            },

            communicationId() {
                if (!empty(this.currentCommunicationId)) {
                    return this.currentCommunicationId;
                }

                return null;
            },
        },

        methods: {
            ...mapActions(useGlobalStore, ['appendNewError']),

            loadItems() {
                this.itemsCount += this.loadMoreCount;
            },

            async loadEmailBody(communication) {
                const index = this.connectedCommunications.findIndex(
                    (c) => c.id == communication.id
                );

                if (typeof communication.email_body !== 'undefined' || index === -1) return;

                this.connectedCommunications[index].loading_body = true;

                const timing = useTimedPageAction({
                    action: TimedPageActions.COMMUNICATION_LIST_EMAIL_MODAL_LOAD,
                    additionalData: {
                        leadId: communication.lead_id || this.$route?.params?.id,
                        communicationId: communication.id,
                    },
                });

                try {
                    await timing.run(async () => {
                        const response = await this.$axios.get(
                            `v1/communications/${communication.id}`,
                            {
                                params: {
                                    from: 'clientCard',
                                },
                            }
                        );

                        if (response.status === 200 && this.connectedCommunications[index]) {
                            this.connectedCommunications[index].email_body =
                                response.data.data.email_body;
                            this.connectedCommunications[index].attachments =
                                response.data.data.attachments;
                        }
                    });
                } catch (error) {
                    if (!error.request) {
                        throw error;
                    }

                    if (error.response && error.response.status === 403) {
                        return;
                    }

                    this.appendNewError({
                        code: '0025',
                        display: true,
                        error,
                    });
                } finally {
                    this.connectedCommunications[index].loading_body = false;
                }
            },

            setConnectedCommunications() {
                this.connectedCommunications = this.communications
                    .filter((communication) => {
                        return (
                            communication.communication_method_id === CommunicationMethod.EMAIL &&
                            ((communication.connected &&
                                communication.service_id === 4 &&
                                !['interrupted', 'error', 'invalid', 'pending'].includes(
                                    communication.status
                                )) ||
                                communication.kind === 'auto')
                        );
                    })
                    .map((communication) => {
                        return {
                            ...communication,
                            loading_body: false,
                            sort_created_at:
                                communication.original_created_at || communication.created_at,
                        };
                    });
            },
        },

        mounted() {
            this.setConnectedCommunications();

            this.$nextTick(() => {
                if (this.$route && this.$route.query.communicationId) {
                    const communicationId = this.$route.query.communicationId;
                    const communication = this.connectedCommunications.find(
                        (communication) => communication.id == communicationId
                    );

                    useClientCardStore().currentCommunicationId = communicationId;

                    if (communication) {
                        this.loadEmailBody(communication);
                    }
                }
            });
        },
    };
</script>
